import React, { Component, ReactNode } from 'react';
import {
	Platform,
	Orientation,
	DeviceType,
	DragDisabledItem,
	IModuleSwiperItem,
	GhostPositionHelper,
	ScrollDistance,
	WriteDirection,
} from 'sofa-reborn-react';
import { Div } from './ModuleSwiperStyle';
import ModuleSwiperItem from './ModuleSwiperItem/ModuleSwiperItem';
import SkapaIconButton from '@ingka/button';
import ChevronDownSmallPaths from '@ingka/ssr-icon/paths/chevron-down-small';
import ChevronUpSmallIconPaths from '@ingka/ssr-icon/paths/chevron-up-small';
import ChevronDownIconPaths from '@ingka/ssr-icon/paths/chevron-down';
import ChevronUpIconPaths from '@ingka/ssr-icon/paths/chevron-up';
import ChevronLeftIconPaths from '@ingka/ssr-icon/paths/chevron-left';
import ChevronLeftSmallIconPaths from '@ingka/ssr-icon/paths/chevron-left-small';
import ChevronRightIconPaths from '@ingka/ssr-icon/paths/chevron-right';
import ChevronRightSmallIconPaths from '@ingka/ssr-icon/paths/chevron-right-small';
import { KompisImage } from '@inter-ikea-kompis/react-components';
import { localeService } from '../../services';

type Props = {
	platform: Platform;
	writeDirection: WriteDirection;
	orientation: Orientation;
	deviceType: DeviceType;
	dragItemId: string;
	moduleItems: Array<IModuleSwiperItem>;
	ghostHelper: GhostPositionHelper;
	selectedIndex: number;
	scrollDistance: ScrollDistance;
	twoColumns: boolean;
	slideIndex: number;
	isScrollable: boolean;
	onSwipe?: (slideIndex: number) => void;
	onDraggedItem?: (event) => void;
	onItemDragStart?: (draggedItemId: string) => void;
	onGhostPositionUpdate?: (ghost: GhostPositionHelper) => void;
	onItemDragDisabled?: (
		draggedDisabledItem: DragDisabledItem,
		ref: React.RefObject<HTMLDivElement>
	) => void;
	onItemSelect?: (selectedItemId: IModuleSwiperItem) => void;
	setSelectedIndex?: (index: number) => void;
	checkScroll?: (direction: string) => void;
	updateScroll?: (direction: string) => void;
};

export default class ModuleSwiper extends Component<Props> {
	private SCROLL_POSITION = { top: 0, left: 0, x: 0, y: 0, distance: 0, startTime: 0 };
	private MOUSE_DOWN = false;
	private ANIMATION = { cssModeFrameID: 0, startTime: 0, velocityFactor: 25, velocityAngle: 30 };

	public render(): ReactNode {
		const { props } = this;
		return (
			<Div
				id="moduleSwiper"
				platform={props.platform}
				orientation={props.orientation}
				deviceType={props.deviceType}
				hasArmrestPicker={false}
				isSafari={false}
				moduleItems={props.moduleItems}
				isScrollable={props.isScrollable}
				onScroll={() =>
					props.checkScroll &&
					props.checkScroll(
						(props.deviceType === 'mobile' && props.orientation === 'portrait') ||
							props.deviceType === 'tablet'
							? 'horizontal'
							: 'vertical'
					)
				}
				onMouseDown={(e) => this.mouseDownHandler(e)}
				onTouchStart={(e) => this.mouseDownHandler(e)}
				onMouseMove={(e) => this.mouseMoveHandler(e)}
				onTouchMove={(e) => this.mouseMoveHandler(e)}
				onMouseUp={() => this.mouseUpHandler()}
				onTouchEnd={() => this.mouseUpHandler()}
				tabIndex={-1}
			>
				{this.getIconButton(
					(props.deviceType === 'mobile' && props.orientation === 'portrait') ||
						props.deviceType === 'tablet'
						? props.writeDirection === 'ltr'
							? 'left'
							: 'right'
						: 'up'
				)}
				{this.getSwiperItems()}
				{this.getIconButton(
					(props.deviceType === 'mobile' && props.orientation === 'portrait') ||
						props.deviceType === 'tablet'
						? props.writeDirection === 'ltr'
							? 'right'
							: 'left'
						: 'down'
				)}
				{this.getImage()}
			</Div>
		);
	}

	private mouseDownHandler(e): void {
		window.cancelAnimationFrame(this.ANIMATION.cssModeFrameID);
		if (this.props.deviceType === 'desktop' && ['moduleSwiper'].includes(e.currentTarget.id)) {
			return;
		}
		this.ANIMATION.startTime = 0;
		this.SCROLL_POSITION.startTime = this.getTime();
		const element = document.getElementById('moduleSwiper');
		if (element) {
			this.SCROLL_POSITION.left = element.scrollLeft;
			this.SCROLL_POSITION.top = element.scrollTop;
		}
		const eventXY = this.getEventXY(e);
		this.SCROLL_POSITION.x = eventXY.x;
		this.SCROLL_POSITION.y = eventXY.y;

		this.MOUSE_DOWN = true;
	}

	private mouseMoveHandler(e): void {
		if (this.MOUSE_DOWN) {
			const element = document.getElementById('moduleSwiper');
			if (element) {
				const eventXY = this.getEventXY(e);
				if (this.isHorizontal()) {
					const dx = eventXY.x - this.SCROLL_POSITION.x;
					this.SCROLL_POSITION.distance = dx;
					element.scrollLeft = this.SCROLL_POSITION.left - dx;
				} else {
					const dy = eventXY.y - this.SCROLL_POSITION.y;
					this.SCROLL_POSITION.distance = dy;
					element.scrollTop = this.SCROLL_POSITION.top - dy;
				}
			}
		}
	}

	private isHorizontal(): boolean {
		return (
			this.props.orientation === 'portrait' && ['mobile', 'tablet'].includes(this.props.deviceType)
		);
	}

	private mouseUpHandler(): void {
		if (this.MOUSE_DOWN) {
			this.animate();
		}
		this.MOUSE_DOWN = false;
	}

	private animate(): void {
		const time = this.getTime();
		if (this.ANIMATION.startTime === 0) {
			this.ANIMATION.startTime = time;
		}

		const duration = this.ANIMATION.startTime - this.SCROLL_POSITION.startTime;
		const progress = Math.max(Math.min((time - this.ANIMATION.startTime) / duration, 1), 0);
		if (progress !== 1) {
			const easeProgress = 0.5 - Math.cos(progress * Math.PI) / 2;
			const targetDistance =
				(Math.pow((this.SCROLL_POSITION.distance / duration) * this.ANIMATION.velocityFactor, 2) *
					Math.sin((2 * this.ANIMATION.velocityAngle * Math.PI) / 180)) /
				9.82;
			const distance = easeProgress * targetDistance;
			const step =
				this.SCROLL_POSITION.distance > 0
					? this.SCROLL_POSITION.distance + distance
					: this.SCROLL_POSITION.distance - distance;

			const element = document.getElementById('moduleSwiper');
			if (element) {
				if (this.isHorizontal()) {
					element.scrollLeft = this.SCROLL_POSITION.left - step;
				} else {
					element.scrollTop = this.SCROLL_POSITION.top - step;
				}
			}
			this.ANIMATION.cssModeFrameID = window.requestAnimationFrame(() => this.animate());
		} else {
			window.cancelAnimationFrame(this.ANIMATION.cssModeFrameID);
		}
	}

	private getEventXY(e) {
		if (e.touches && e.touches.length) {
			return {
				x: e.touches[0].pageX,
				y: e.touches[0].pageY,
			};
		}
		return {
			x: e.clientX,
			y: e.clientY,
		};
	}

	private getTime(): number {
		return new Date().getTime();
	}

	private getImage() {
		const { props } = this;
		return (
			<KompisImage
				src={props.ghostHelper.imgSrc}
				// @ts-expect-error Kompis elemenets support style but it isn't exposed
				style={{
					position: 'fixed',
					top: props.ghostHelper.y - 50,
					left: props.ghostHelper.x - 50,
					zIndex: props.ghostHelper.z,
					display: props.dragItemId ? 'block' : 'none',
					width: '100px',
					height: '100px',
				}}
			></KompisImage>
		);
	}

	private getIconButton(direction: string) {
		const { props } = this;
		const icon = {
			up: props.platform === 'kiosk' ? ChevronUpIconPaths : ChevronUpSmallIconPaths,
			down: props.platform === 'kiosk' ? ChevronDownIconPaths : ChevronDownSmallPaths,
			left: props.platform === 'kiosk' ? ChevronLeftIconPaths : ChevronLeftSmallIconPaths,
			right: props.platform === 'kiosk' ? ChevronRightIconPaths : ChevronRightSmallIconPaths,
		};
		let arrowDisabled = false;
		let scrollWidth = 0;
		if (props.orientation === 'portrait') {
			const swiper = document.getElementById('moduleSwiper');
			if (swiper) {
				scrollWidth = swiper.scrollWidth - swiper.clientWidth;
				arrowDisabled = scrollWidth + props.scrollDistance.lef < 1;
			}
		} else {
			arrowDisabled = props.scrollDistance.lef > 0;
		}
		const disabled = {
			up: props.scrollDistance.top === 0,
			down: props.scrollDistance.bot > 99 || !props.isScrollable,
			left:
				props.writeDirection === 'ltr'
					? props.scrollDistance.lef === 0
					: arrowDisabled || !props.isScrollable,
			right:
				props.writeDirection === 'ltr'
					? props.scrollDistance.rig > 99 || !props.isScrollable
					: props.scrollDistance.lef === 0,
		};
		const label = {
			up: 'moduleSwiperArrowUp',
			down: 'moduleSwiperArrowDown',
			left: 'moduleSwiperArrowLeft',
			right: 'moduleSwiperArrowRight',
		};
		const padding =
			direction === 'up'
				? '4px 0 0 0'
				: direction === 'down'
				? '0 0 4px 0'
				: direction === 'left'
				? '0 0 0 4px'
				: props.writeDirection === 'ltr'
				? '0'
				: '0 4px 0 0';
		const margin = direction === 'down' ? (props.platform === 'kiosk' ? '4px' : '2px') : '0';
		const right = this.getRightValue(
			direction,
			props.writeDirection,
			props.deviceType,
			props.orientation,
			props.platform
		);
		const left =
			props.deviceType === 'desktop' && props.writeDirection === 'ltr'
				? 'calc(50% - 20px)'
				: props.platform === 'kiosk' && props.writeDirection === 'ltr'
				? 'calc(50% - 25px)'
				: props.deviceType === 'mobile' &&
				  props.orientation === 'landscape' &&
				  props.writeDirection === 'ltr'
				? 'calc(50% - 20px)'
				: 'auto';
		const displayButton = props.platform === 'kiosk' && disabled[direction] ? 'none' : 'flex';
		const styles = {
			padding: padding,
			margin: margin,
			left: left,
			right: right,
			display: displayButton,
		};
		return (
			<SkapaIconButton
				id={'iconButton'}
				iconOnly
				ssrIcon={icon[direction]}
				small={true}
				type={'primary'}
				disabled={disabled[direction]}
				aria-label={localeService.getTranslation(label[direction])}
				onClick={() => {
					props.updateScroll && props.updateScroll(direction);
				}}
				style={styles}
			/>
		);
	}

	private getRightValue(
		direction: string,
		writeDirection: WriteDirection,
		deviceType: DeviceType,
		orientation: Orientation,
		platform: Platform
	) {
		if (direction === 'left') {
			return writeDirection === 'ltr' ? 'auto' : deviceType === 'mobile' ? '88%' : '93%';
		} else if (direction === 'right') {
			return writeDirection === 'ltr' ? '0px' : 'auto';
		} else {
			return ((deviceType === 'mobile' && orientation === 'landscape') ||
				deviceType === 'desktop') &&
				writeDirection === 'rtl'
				? 'calc(50% - 20px)'
				: platform === 'kiosk' && writeDirection === 'rtl'
				? 'calc(50% - 25px)'
				: 'auto';
		}
	}

	private getSwiperItems() {
		const { props } = this;
		return props.moduleItems.map((module, index) => {
			return (
				<ModuleSwiperItem
					key={index}
					platform={props.platform}
					orientation={props.orientation}
					writeDirection={props.writeDirection}
					deviceType={props.deviceType}
					moduleItem={module}
					id={module.id}
					isDragged={module.id === props.dragItemId}
					twoColumns={props.twoColumns}
					isFirst={index === 0}
					isLast={index === props.moduleItems.length - 1}
					onItemDragStart={(draggedItemId: string) => {
						this.MOUSE_DOWN = false;
						props.onItemDragStart && props.onItemDragStart(draggedItemId);
					}}
					onGhostPositionUpdate={(ghost: GhostPositionHelper) =>
						props.onGhostPositionUpdate && props.onGhostPositionUpdate(ghost)
					}
					onItemDragDisabled={(
						draggedDisabledItem: DragDisabledItem,
						ref: React.RefObject<HTMLDivElement>
					) => props.onItemDragDisabled && props.onItemDragDisabled(draggedDisabledItem, ref)}
					onItemSelect={(selectedItemId: IModuleSwiperItem): void => {
						props.onItemSelect && props.onItemSelect(selectedItemId);
					}}
				></ModuleSwiperItem>
			);
		});
	}
}
